import clsx from 'clsx';

import { useUserSettings } from '../../../shared';
import { Collapser, LessonWrapper, LessonComponent } from '../../../components';
import type { CourseType, EnrollmentType } from '../useCourse';

type SideNavProps = {
  course: CourseType;
  enrollment: EnrollmentType;

  stepId?: string;
  updating: boolean;
  collapsable: boolean;
  onChangeStep: (step: string) => void;
};

type IconProps = {
  collapsed: boolean;
  onClick: () => void;

  top: number;
  right: number;
};

const Icon: React.FC<IconProps> = ({
  top,
  right,

  collapsed,
  onClick,
}) => {
  return (
    <svg
      width="20"
      height="32"
      viewBox="0 0 20 32"
      fill="none"
      onClick={onClick}
      xmlns="http://www.w3.org/2000/svg"
      style={{ right: right, top: top + 35 }}
      className={clsx(
        collapsed ? 'rotate-180 rounded-l-sm' : 'rounded-r-sm',
        'absolute cursor-pointer bg-[#3A3A40]'
      )}
    >
      <path
        d="M11.75 11.5L7.25 16L11.75 20.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const SideNav: React.FC<SideNavProps> = ({
  course,
  enrollment,

  stepId,
  updating,
  collapsable = false,

  onChangeStep,
}) => {
  const [col, setCollapsed] = useUserSettings(false, 'course_side_nav');
  const collapsed = collapsable ? col : false;

  return (
    <div
      className={clsx(
        'relative h-full',
        collapsable
          ? !collapsed
            ? 'min-w-[25rem] w-[45rem] border-r border-r-accordionBorder'
            : 'w-0'
          : '',
        collapsable ? 'transition-all' : ''
      )}
    >
      {!!collapsable && (
        <Icon
          onClick={() => setCollapsed(c => !c)}
          collapsed={collapsed}
          top={10}
          right={-20}
        />
      )}
      <div
        className={clsx(
          'overflow-y-auto max-h-full',
          collapsed ? 'opacity-0' : 'opacity-1'
        )}
      >
        {(course.modules || []).map((module, index) => {
          const isCurrentModule = Boolean(
            stepId && module.steps?.some?.(s => s.id === stepId)
          );

          return (
            <Collapser
              key={index}
              contentClassName="py-sm"
              forceOpen={isCurrentModule}
              header={`${index + 1}. ${module.title}`}
              defaultExpanded={stepId ? isCurrentModule : !index}
            >
              {(module.steps || []).map((lesson, lIndex) => {
                const completed = !!enrollment.completedSteps?.includes?.(
                  lesson.id!
                );

                return (
                  <LessonWrapper
                    key={lIndex}
                    completed={completed}
                    active={stepId === lesson.id}
                    onClick={() => {
                      !updating && onChangeStep(lesson?.id ?? '');
                    }}
                  >
                    <LessonComponent
                      lesson={lesson}
                      number={lIndex + 1}
                      completed={completed}
                      active={stepId === lesson.id}
                    />
                  </LessonWrapper>
                );
              })}
            </Collapser>
          );
        })}
      </div>
    </div>
  );
};
